import { useRef, useState } from "react";
import api, { productImg } from "@/lib/api";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline image picker — combines preview, drag-drop upload, file picker
 * and optional "paste URL" fallback. Designed for single-image fields in the CMS.
 */
export const ImageField = ({ value, onChange, label, testid, aspect = "square" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const aspectCls = aspect === "wide" ? "aspect-[4/3]" : aspect === "tall" ? "aspect-[3/4]" : "aspect-square";

  const upload = async (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "video/mp4", "video/webm", "video/quicktime"];
    if (!allowed.includes(file.type)) { toast.error("JPG/PNG/GIF/WebP/MP4/WebM only"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Max 25 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    upload(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-slate-600 font-bold">{label}</span>
      <div className="mt-1.5 flex gap-3">
        <div className={`w-24 ${aspectCls} rounded-2xl glass overflow-hidden flex-shrink-0 relative`}>
          {value ? (
            <img src={productImg(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-pink-300">
              <ImagePlus className="w-7 h-7" />
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-pink-600" />
            </div>
          )}
        </div>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testid}-drop`}
          className={`flex-1 cursor-pointer rounded-2xl border-2 border-dashed transition px-3 py-3 text-center flex flex-col justify-center ${
            dragging ? "border-pink-600 bg-pink-50" : "border-pink-200 bg-white/40 hover:bg-white/70"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
            data-testid={`${testid}-input`}
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
          />
          <div className="text-xs font-bold text-pink-950">
            {uploading ? "Uploading…" : "Drop or click to upload"}
          </div>
          <p className="text-[10px] text-slate-600 mt-0.5">JPG · PNG · WebP · MP4 · WebM · max 25MB</p>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowUrl(!showUrl); }}
            data-testid={`${testid}-toggle-url`}
            className="mt-1.5 text-[10px] text-pink-600 hover:underline"
          >
            {showUrl ? "Hide URL field" : "Or paste URL"}
          </button>
        </div>
      </div>
      {showUrl && (
        <input
          data-testid={`${testid}-url`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="mt-2 w-full bg-white/70 border border-white/60 rounded-2xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
        />
      )}
    </div>
  );
};
